import { DonationAmount, useCustomEventListener } from '@classy/campaign-page-blocks'
import { EVENT } from 'models/event'
import { convertAmount } from 'features/Passport/convertAmounts'
import { produce } from 'immer'

/**
 * Converts the passed amounts to the selected currency code (Passport feature).
 *
 * Updates Donation block's donationAmount, recurringAmounts, minDonationAmount,
 * and maxDonationAmount props.
 *
 * Uses immer to create new object references. React hooks only trigger when references change.
 */
export const usePassportCurrencyConversion = (
  initialDonationAmounts: DonationAmount[],
  initialRecurringAmounts: DonationAmount[],
  initialMinAmount: number,
  initialMaxAmount: number,
  currencyCode: string,
) => {
  const currencyDetails = useCustomEventListener(EVENT.PASSPORT_GET_CURRENCY_CONVERSION, {
    oldCurrencyCode: currencyCode,
    newCurrencyCode: currencyCode,
    currencyConversionRate: 1,
    isLoading: false,
  })

  if (currencyDetails.isLoading) {
    return {
      donationAmounts: initialDonationAmounts,
      recurringAmounts: initialRecurringAmounts,
      minDonationAmount: initialMinAmount,
      maxDonationAmount: initialMaxAmount,
      currencyCode: currencyDetails.newCurrencyCode,
      isLoading: currencyDetails.isLoading,
    }
  }

  const currencyConfig = {
    conversionRate: currencyDetails.currencyConversionRate,
    campaignCurrencyCode: currencyCode,
    selectedCurrencyCode: currencyDetails.newCurrencyCode,
  }

  const donationAmounts = produce(initialDonationAmounts, (draft) => {
    return draft.map((donationAmount) => ({
      ...donationAmount,
      amount: convertAmount({ ...currencyConfig, amount: donationAmount.amount }),
    }))
  })

  const recurringAmounts = produce(initialRecurringAmounts, (draft) => {
    return draft.map((recurringAmount) => ({
      ...recurringAmount,
      amount: convertAmount({ ...currencyConfig, amount: recurringAmount.amount }),
    }))
  })

  const minDonationAmount = produce(initialMinAmount, () =>
    convertAmount({
      ...currencyConfig,
      amount: initialMinAmount,
    }),
  )

  const maxDonationAmount = produce(initialMaxAmount, () =>
    convertAmount({
      ...currencyConfig,
      amount: initialMaxAmount,
    }),
  )

  return {
    donationAmounts,
    recurringAmounts,
    minDonationAmount,
    maxDonationAmount,
    currencyCode: currencyDetails.newCurrencyCode,
    isLoading: currencyDetails.isLoading,
  }
}
